import React, { useState, useEffect, ChangeEvent } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { resetPassword } from '../api/auth';
import { useAuth } from '../context/AuthContext';
import { TextField } from './TextField';
import { Button } from './Button';
import { ROUTES } from '../config/routes';

const AcceptInvitationForm: React.FC = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [token, setToken] = useState('');
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const tokenFromUrl = searchParams.get('token');
    const emailFromUrl = searchParams.get('email');

    if (tokenFromUrl) {
      setToken(tokenFromUrl);
    } else {
      setError('Invalid invitation link');
      return;
    }

    // Email is optional - if not provided, user will need to log in manually after password reset
    if (emailFromUrl) {
      setEmail(emailFromUrl);
    }
  }, [location]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    try {
      // First, reset the password
      await resetPassword(token, password);

      // If email is provided, automatically log the user in
      if (email) {
        // Replace spaces with plus signs in email (URL decoding converts + to space)
        const emailWithPlus = email.replace(/ /g, '+');
        await login({ Email: emailWithPlus, Password: password });
        // Navigate to home after successful login
        navigate(ROUTES.HOME());
      } else {
        // If no email, redirect to login page
        navigate(ROUTES.LOGIN() + '?invitationAccepted=true');
      }
    } catch (error: any) {
      console.error('Accept invitation error:', error);
      if (error.response?.status === 401) {
        setError('Failed to set password. The invitation link may have expired.');
      } else if (error.response?.status === 400 && error.response?.data?.message) {
        setError(error.response.data.message);
      } else {
        setError('Failed to set up your account. Please try again.');
      }
      setLoading(false);
    }
  };

  if (error && !token) {
    return (
      <div className="container h-screen flex flex-col gap-4 items-center justify-center text-red">
        {error}
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="bg-white rounded-3xl w-full p-4 md:max-w-[424px]"
    >
      <h2 className="text-primary text-center font-semibold mb-2">
        Accept Invitation
      </h2>
      <p className="text-gray-600 text-center text-sm mb-6">
        To get access to the dashboard, please set up a password for your account
      </p>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
          {error}
        </div>
      )}

      <TextField
        type="password"
        label="New Password"
        className="mb-4"
        value={password}
        onChange={(e: ChangeEvent<HTMLInputElement>) =>
          setPassword(e.target.value)
        }
        required
        disabled={loading}
      />
      <TextField
        type="password"
        label="Confirm Password"
        value={confirmPassword}
        onChange={(e: ChangeEvent<HTMLInputElement>) =>
          setConfirmPassword(e.target.value)
        }
        className="mb-24"
        required
        disabled={loading}
      />
      <Button type="submit" size="full" disabled={loading}>
        {loading ? 'Setting up...' : 'Set Password & Continue'}
      </Button>
      <Link
        to={ROUTES.LOGIN()}
        className="text-primary underline text-center mt-4 block"
      >
        Back to Login
      </Link>
    </form>
  );
};

export default AcceptInvitationForm;
